// Motion utilities for SEICon III premium-motion-v3.
// All hooks are GPU-friendly (transform + opacity only) and respect prefers-reduced-motion.
import React, { useEffect, useRef, useState, useCallback, createContext, useContext } from 'react';

// ---------- Context ----------
const MotionContext = createContext({
  motion: null,
  prefersReducedMotion: false,
  isMobile: false,
  isTouch: false,
  speedMul: 1,
});

export function MotionProvider({ motion, children }) {
  const [prefersReducedMotion, setPRM] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const upd = () => setPRM(mq.matches);
    upd();
    mq.addEventListener?.('change', upd);
    const mmq = window.matchMedia('(max-width: 767px)');
    const updM = () => setIsMobile(mmq.matches);
    updM();
    mmq.addEventListener?.('change', updM);
    setIsTouch('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);
    return () => {
      mq.removeEventListener?.('change', upd);
      mmq.removeEventListener?.('change', updM);
    };
  }, []);

  // Speed multiplier: slow=1.3, normal=1.0, fast=0.75
  const speed = motion?.speed || 'normal';
  const speedMul = speed === 'slow' ? 1.3 : speed === 'fast' ? 0.75 : 1.0;

  // Apply CSS var for global duration scaling
  useEffect(() => {
    document.documentElement.style.setProperty('--motion-speed', String(speedMul));
  }, [speedMul]);

  const masterEnabled = motion?.master_enabled !== false;
  const mobileBehavior = motion?.mobile_behavior || 'reduced';
  // Effective motion enabled
  const motionAllowed =
    masterEnabled &&
    !prefersReducedMotion &&
    !(isMobile && mobileBehavior === 'off');

  return (
    <MotionContext.Provider
      value={{ motion, prefersReducedMotion, isMobile, isTouch, speedMul, motionAllowed, mobileBehavior }}
    >
      {children}
    </MotionContext.Provider>
  );
}

export const useMotion = () => useContext(MotionContext);

// ---------- Reveal hook (IntersectionObserver, fires once) ----------
export function useReveal(options = {}) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const { motionAllowed, isMobile, mobileBehavior } = useMotion();
  const threshold = options.threshold ?? 0.2;

  useEffect(() => {
    if (!ref.current) return;
    // If motion disallowed, instantly reveal
    if (!motionAllowed) { setRevealed(true); return; }
    // If mobile + reduced, still reveal with opacity-only path
    const node = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setRevealed(true);
            io.disconnect();
          }
        }
      },
      { threshold, rootMargin: '0px 0px -10% 0px' }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold, motionAllowed]);

  const simple = isMobile && mobileBehavior === 'reduced';
  return { ref, revealed, simple };
}

// ---------- Number ticker with exponential-out easing ----------
export function useNumberTicker(target, { duration = 1600, enabled = true } = {}) {
  const [value, setValue] = useState(0);
  const { motionAllowed, speedMul } = useMotion();
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!ref.current || startedRef.current) return;
    if (!enabled || !motionAllowed) { setValue(target); startedRef.current = true; return; }
    const node = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const dur = duration * speedMul;
            const start = performance.now();
            const step = (now) => {
              const t = Math.min(1, (now - start) / dur);
              // expo out
              const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
              setValue(Math.round(target * eased));
              if (t < 1) requestAnimationFrame(step);
              else setValue(target);
            };
            requestAnimationFrame(step);
            io.disconnect();
          }
        }
      },
      { threshold: 0.3 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [target, duration, enabled, motionAllowed, speedMul]);

  return { ref, value };
}

// ---------- Magnetic cursor ----------
export function useMagnetic(enabled = true, radius = 80, pull = 6) {
  const ref = useRef(null);
  const { motionAllowed, isTouch } = useMotion();
  useEffect(() => {
    if (!ref.current || !enabled || !motionAllowed || isTouch) return;
    const el = ref.current;
    let raf = 0;
    let tx = 0, ty = 0;
    let curX = 0, curY = 0;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist < radius) {
        const f = (radius - dist) / radius; // 0..1
        tx = (dx / radius) * pull * f;
        ty = (dy / radius) * pull * f;
      } else {
        tx = 0; ty = 0;
      }
    };
    const onLeave = () => { tx = 0; ty = 0; };
    const loop = () => {
      curX += (tx - curX) * 0.15;
      curY += (ty - curY) * 0.15;
      el.style.transform = `translate3d(${curX.toFixed(2)}px, ${curY.toFixed(2)}px, 0)`;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    window.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      el.style.transform = '';
    };
  }, [enabled, motionAllowed, isTouch, radius, pull]);
  return ref;
}

// ---------- 3D card tilt ----------
export function useTilt(enabled = true, maxTilt = 6) {
  const ref = useRef(null);
  const { motionAllowed, isTouch } = useMotion();
  useEffect(() => {
    if (!ref.current || !enabled || !motionAllowed || isTouch) return;
    const el = ref.current;
    let raf = 0;
    let rx = 0, ry = 0;
    let curRX = 0, curRY = 0;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width;  // 0..1
      const py = (e.clientY - r.top) / r.height;
      ry = (px - 0.5) * 2 * maxTilt;   // rotateY
      rx = -(py - 0.5) * 2 * maxTilt;  // rotateX
      el.style.setProperty('--mx', `${(px * 100).toFixed(2)}%`);
      el.style.setProperty('--my', `${(py * 100).toFixed(2)}%`);
    };
    const onLeave = () => { rx = 0; ry = 0; };
    const loop = () => {
      curRX += (rx - curRX) * 0.18;
      curRY += (ry - curRY) * 0.18;
      el.style.transform = `perspective(1000px) rotateX(${curRX.toFixed(2)}deg) rotateY(${curRY.toFixed(2)}deg) translateZ(0)`;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      el.style.transform = '';
      el.style.removeProperty('--mx');
      el.style.removeProperty('--my');
    };
  }, [enabled, motionAllowed, isTouch, maxTilt]);
  return ref;
}

// ---------- Cursor spotlight (positions a CSS var on container) ----------
export function useCursorSpotlight(enabled = true) {
  const ref = useRef(null);
  const { motionAllowed, isTouch } = useMotion();
  useEffect(() => {
    if (!ref.current || !enabled || !motionAllowed || isTouch) return;
    const el = ref.current;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      el.style.setProperty('--spot-x', `${x}px`);
      el.style.setProperty('--spot-y', `${y}px`);
      el.style.setProperty('--spot-opacity', '1');
    };
    const onLeave = () => { el.style.setProperty('--spot-opacity', '0'); };
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [enabled, motionAllowed, isTouch]);
  return ref;
}

// ---------- Lazy in-viewport video (autoplay when in viewport, pause when out) ----------
export function useInViewportVideo() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const v = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { v.play().catch(() => {}); }
          else { v.pause(); }
        }
      },
      { threshold: 0.2 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);
  return ref;
}
